import type { Action, ActionOn, Thunk, Computed } from 'easy-peasy';
import { actionOn, action, thunk, computed } from 'easy-peasy';
import type { Injections } from '../services/injections';
import type { StoreModel } from '..';
import type {
  CarrierSelectionTableSummary,
  Simulation,
  SimulationSummary,
  SimulationUpdate,
  SimulationSetupData,
  SimulationDetail,
  CreateSimulationBatchesReponse,
  SimulationServiceVersion,
  SimulationRawResult,
  SimulationRawResultOptions,
  SimulationBatchUpdate,
  SimulationShipmentBatch,
  SimulationExportMetadata,
  SimulationContextMappingCreate,
  HistoricalShipmentSelectData,
  SimulationContextMapping,
  FetchSimulationReportParams,
  SimulationReport,
  SimulationReportCollection,
  SimulationReportType,
  SimulationSellRateMapping,
  SimulationSellRateZoneMapping,
  CarrierZoneToSellRateZoneMapping,
  SearchCarrierZoneToSellRateZoneMappingParams,
  DeleteSellRateMappingsRes,
} from './types';
import type { CarrierSelectionSummary } from '../services/DatalakeService/types';
import type { ApplicationErrorOptions } from '../../types';
import parseStatusCode from '../errors/parseStatusCode';
import { SHIPMENT_SELECTION_KEY } from '../../components/simulation/shared/SimulationShipmentSelectStep';
import { getPayloadForSimulationType } from './utils';
import { simulationV2 } from '~/services/simulation-service/simulation';
import { resolveSimulationServiceVersion } from './resolveSimulationServiceVersion';

export interface SimulationModel {
  lastAction?: number;
  simulationActionStart: Action<SimulationModel, void>;
  simulationActionFailure: Action<SimulationModel, ApplicationErrorOptions>;
  setSimulationDetail: Action<SimulationModel, SimulationDetail>;
  setSimulation: Action<SimulationModel, Simulation>;
  setSimulations: Action<SimulationModel, Simulation[]>;
  fetchSimulations: Thunk<SimulationModel, any, Injections, StoreModel, Promise<Simulation[]>>;
  fetchSimulationDetail: Thunk<SimulationModel, any, Injections, StoreModel, Promise<SimulationDetail>>;
  fetchSimulationRawResults: Thunk<
    SimulationModel,
    SimulationRawResultOptions,
    Injections,
    StoreModel,
    Promise<SimulationRawResult[]>
  >;
  simulations: Record<string, Simulation>;
  simulationDetails: Record<string, SimulationDetail>;
  simulationExports?: Record<string, SimulationExportMetadata>;
  simulationReports: Record<string, SimulationReportCollection>;
  simulationSummaries: Record<string, Simulation>;
  simulationRawResult?: SimulationRawResult;
  setSimulationExports: Action<SimulationModel, SimulationExportMetadata[]>;
  setSimulationSummaries: Action<SimulationModel, Simulation[]>;
  setSimulationRawResult: Action<SimulationModel, SimulationRawResult>;
  setSimulationReport: Action<SimulationModel, SimulationReport>;

  // TODO define search params
  // Do we need to filter by status?
  // limit the number of simulations to fetch?
  // Only include active or recently active?
  fetchSimulationReport: Thunk<
    SimulationModel,
    FetchSimulationReportParams,
    Injections,
    StoreModel,
    Promise<SimulationReport>
  >;
  fetchSimulationSummaries: Thunk<SimulationModel, any, Injections, StoreModel, Promise<SimulationSummary[]>>;
  fetchSimulationExports: Thunk<
    SimulationModel,
    { simulationId: string },
    Injections,
    StoreModel,
    Promise<SimulationExportMetadata[]>
  >;
  fetchCarrierSelectionSummaries: Thunk<
    SimulationModel,
    any,
    Injections,
    StoreModel,
    Promise<CarrierSelectionSummary[]>
  >;
  setCarrierSelectionSummaries: Action<SimulationModel, CarrierSelectionSummary[]>;
  carrierSelectionSummaries: CarrierSelectionSummary[];
  carrierSelectionTableSummaries: Computed<SimulationModel, CarrierSelectionTableSummary[]>;
  getSimulationReport: Computed<
    SimulationModel,
    (simulationId: string, simulationReportType: SimulationReportType) => SimulationReport
  >;
  activeNavigationStep: number;
  setActiveNavigationStep: Action<SimulationModel, number>;
  createSimulation: Thunk<SimulationModel, Partial<Simulation>, Injections, StoreModel, Promise<Simulation>>;
  updateSimulation: Thunk<SimulationModel, SimulationUpdate, Injections, StoreModel, Promise<Simulation>>;
  updateSimulationShipmentBatch: Thunk<
    SimulationModel,
    SimulationBatchUpdate,
    Injections,
    StoreModel,
    Promise<SimulationShipmentBatch>
  >;
  simulationSetupData: SimulationSetupData;
  setSimulationSetupData: Action<SimulationModel, SimulationSetupData>;
  createSimulationContexts: Thunk<
    SimulationModel,
    SimulationContextMappingCreate[],
    Injections,
    StoreModel,
    Promise<SimulationContextMapping[]>
  >;
  createSimulationBatches: Thunk<
    SimulationModel,
    string,
    Injections,
    StoreModel,
    Promise<CreateSimulationBatchesReponse>
  >;
  // not sim specific need to think about where these kinds of things live
  fulfillmentContextIDsByMethod: Record<string, Record<string, string[]>>;
  fetchFulfillmentContextIDsByMethod: Thunk<
    SimulationModel,
    { carrierId: string; serviceMethodId: string },
    Injections,
    StoreModel,
    Promise<string[]>
  >;
  setFulfillmentContextIDsByMethod: Action<
    SimulationModel,
    { carrierId: string; serviceMethodId: string; fulfillmentContextIds: string[] }
  >;

  fetchSimulationSellRateMappings: Thunk<
    SimulationModel,
    string,
    Injections,
    StoreModel,
    Promise<SimulationSellRateMapping>
  >;
  setSimulationSellRateMappings: Action<SimulationModel, SimulationSellRateMapping[]>;
  simulationSellRateMappings: Record<string, SimulationSellRateMapping[]>;
  usingCarrierSelectionData: Computed<SimulationModel, boolean>;
  archiveSimulation: Thunk<SimulationModel, string, Injections, StoreModel, Promise<Simulation>>;
  startSimulation: Thunk<SimulationModel, Simulation, Injections, StoreModel, Promise<Simulation>>;
  onNavigationStepChange: ActionOn<SimulationModel>;
  // mappings for a simulation
  fetchSimulationSellRateZoneMappings: Thunk<
    SimulationModel,
    { simulationId: string; sellCarrierRateId: string },
    Injections,
    StoreModel,
    Promise<any>
  >;
  setSimulationSellRateZoneMappings: Action<SimulationModel, any>;
  simulationSellRateZoneMappings: Record<string, SimulationSellRateZoneMapping[]>;
  //raw search of mappings
  fetchCarrierSellRateZoneMappings: Thunk<
    SimulationModel,
    SearchCarrierZoneToSellRateZoneMappingParams,
    Injections,
    StoreModel,
    Promise<CarrierZoneToSellRateZoneMapping[]>
  >;
  setCarrierSellRateZoneMappings: Action<SimulationModel, CarrierZoneToSellRateZoneMapping[]>;
  carrierSellRateZoneMappings: CarrierZoneToSellRateZoneMapping[];
  deleteCarrierSellRateMappings: Thunk<
    SimulationModel,
    Partial<SearchCarrierZoneToSellRateZoneMappingParams>,
    Injections,
    StoreModel,
    Promise<DeleteSellRateMappingsRes>
  >;

  createSimulationSellRateZoneMappings: Thunk<
    SimulationModel,
    Partial<CarrierZoneToSellRateZoneMapping>[],
    Injections,
    StoreModel,
    Promise<Partial<CarrierZoneToSellRateZoneMapping>[]>
  >;
}

export const simulationModel: SimulationModel = {
  activeNavigationStep: 0,
  simulationSetupData: {},
  simulationDetails: {},
  simulationReports: {},
  simulationSellRateMappings: {},
  simulationRawResult: undefined,
  fulfillmentContextIDsByMethod: {},
  simulationExports: {},
  usingCarrierSelectionData: computed((state) => {
    return (
      (state?.simulationSetupData?.[SHIPMENT_SELECTION_KEY] as HistoricalShipmentSelectData)?.carrierSelectionSummaries
        ?.length > 0
    );
  }),
  setActiveNavigationStep: action((state, payload) => {
    state.activeNavigationStep = payload;
  }),
  simulations: {},
  simulationSellRateZoneMappings: {},
  simulationActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  simulationActionFailure: action((state, payload) => {
    console.log(payload);
  }),
  setSimulationReport: action((state, payload) => {
    if (!state.simulationReports[payload.simulationId]) {
      state.simulationReports[payload.simulationId] = { [payload.reportType]: payload } as any;
    } else {
      state.simulationReports[payload.simulationId][payload.reportType] = payload;
    }
  }),
  setSimulationSetupData: action((state, payload) => {
    state.simulationSetupData = payload;
  }),
  setSimulation: action((state, payload) => {
    state.simulations = {
      ...state.simulations,
      [payload.simulationId]: payload,
    };
  }),
  setSimulationRawResult: action((state, payload) => {
    state.simulationRawResult = payload;
  }),
  setSimulationDetail: action((state, payload) => {
    state.simulationDetails = {
      ...state.simulationDetails,
      [payload.simulationId]: payload,
    };
  }),
  setSimulations: action((state, payload) => {
    state.simulations = {};
    for (const sim of payload) {
      state.simulations[sim.simulationId] = sim;
    }
  }),
  setSimulationExports: action((state, payload) => {
    state.simulationExports = {};
    for (const val of payload) {
      state.simulationExports[val.key] = val;
    }
  }),
  fetchSimulationExports: thunk(async (actions, payload, { injections }) => {
    try {
      const { simulationId } = payload;
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.fetchSimulationExports(simulationId);
      actions.setSimulationExports(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSimulationReport: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.fetchSimulationReport(payload);
      actions.setSimulationReport(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSimulations: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.fetchSimulations({});
      actions.setSimulations(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSimulationDetail: thunk(async (actions, simulationId, { injections, getStoreState }) => {
    try {
      actions.simulationActionStart();
      const stamp = getStoreState().simulation.simulationSetupData;
      const version = resolveSimulationServiceVersion(stamp);

      let res: SimulationDetail;
      let servedBy: SimulationServiceVersion;
      if (version === 'v2') {
        // v2 first; a 404 means the sim lives only in the v1 store (unstamped deep-link).
        try {
          res = await simulationV2.detail.fn(simulationId);
          servedBy = 'v2';
        } catch (v2Error) {
          const status = (v2Error as { response?: { status?: number } })?.response?.status;
          if (status !== 404) throw v2Error;
          res = await injections.simulationService.fetchSimulationDetail(simulationId);
          servedBy = 'v1';
        }
      } else {
        res = await injections.simulationService.fetchSimulationDetail(simulationId);
        servedBy = 'v1';
      }

      // Stamp the discovered (ground-truth) version into setupData so later calls resolve directly.
      // The hydration effect propagates it; persistence stays lazy (this read path must not write).
      res = { ...res, simulationSetupData: { ...(res.simulationSetupData ?? {}), serviceVersion: servedBy } };

      actions.setSimulationDetail(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  fetchSimulationRawResults: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const { simulationId, date, hour, status, page } = payload;
      const res = await simulationService.fetchSimulationRawResults(simulationId, date, hour, status, page);
      actions.setSimulationRawResult(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  simulationSummaries: {},
  setSimulationSummaries: action((state, payload) => {
    state.simulationSummaries = {};
    for (const sim of payload) {
      state.simulationSummaries[sim.simulationId] = sim;
    }
  }),
  // after reviewing this information in the landing table and the data models
  // this appears that it will require its own route seperate from fetching simulations
  fetchSimulationSummaries: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const partnerId = getStoreState().session.user?.sessionPartnerId ?? '';
      const res = await simulationService.fetchSimulationSummaries(partnerId);
      actions.setSimulationSummaries(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  carrierSelectionSummaries: [],
  fetchCarrierSelectionSummaries: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      actions.simulationActionStart();
      const { datalakeService } = injections;
      const partnerId = getStoreState().session.user?.sessionPartnerId ?? '';
      const res = await datalakeService.fetchCarrierSelectionSummaries({ partnerId, ...payload });
      actions.setCarrierSelectionSummaries(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      return error;
    }
  }),
  setCarrierSelectionSummaries: action((state, payload) => {
    state.carrierSelectionSummaries = payload;
  }),
  carrierSelectionTableSummaries: computed((state) => {
    return Object.values(
      state.carrierSelectionSummaries.reduce((prev: Record<string, CarrierSelectionTableSummary>, val) => {
        if (prev[val.shippedDate]) {
          prev[val.shippedDate].contexts.add(val.fulfillmentContextId);
          prev[val.shippedDate] = {
            ...prev[val.shippedDate],
            carrierSelectionCount: val.carrierSelectionCount + prev[val.shippedDate].carrierSelectionCount,
          };
        } else {
          prev[val.shippedDate] = {
            shippedDate: val.shippedDate,
            carrierSelectionCount: val.carrierSelectionCount,
            contexts: new Set([val.fulfillmentContextId]),
          };
        }
        return prev;
      }, {}),
    );
  }),
  getSimulationReport: computed((state) => (simulationId: string, simulationReportType: SimulationReportType) => {
    return state.simulationReports[simulationId]?.[simulationReportType];
  }),
  createSimulation: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.createSimulation(payload);
      actions.setSimulation(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  updateSimulation: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.updateSimulation(payload);
      // this is a temporary fix for stale workflow data
      // we need to think about what we are doing with this duel approach here
      // we are storing both the decorated and non-decorated simulations
      actions.fetchSimulationDetail(payload.simulationId);
      actions.setSimulation(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  updateSimulationShipmentBatch: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.updateSimulationShipmentBatch(payload);
      actions.fetchSimulationDetail(payload.simulationId);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  createSimulationContexts: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { simulationService } = injections;
      const res = await simulationService.createSimulationContextMappings(payload);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  createSimulationBatches: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const res = await injections.simulationService.createSimulationBatches(payload);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  setFulfillmentContextIDsByMethod: action((state, payload) => {
    state.fulfillmentContextIDsByMethod = {
      ...state.fulfillmentContextIDsByMethod,
      [payload.carrierId]: {
        ...(state.fulfillmentContextIDsByMethod[payload.carrierId] ?? {}),
        [payload.serviceMethodId]: payload.fulfillmentContextIds,
      },
    };
  }),
  fetchFulfillmentContextIDsByMethod: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.simulationService.fetchFulfillmentContextIDsByMethod(
        payload.carrierId,
        payload.serviceMethodId,
      );
      actions.setFulfillmentContextIDsByMethod({
        carrierId: payload.carrierId,
        serviceMethodId: payload.serviceMethodId,
        fulfillmentContextIds: res.fulfillmentContextIds,
      });
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  archiveSimulation: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const res = await injections.simulationService.archiveSimulation(payload);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  onNavigationStepChange: actionOn(
    (actions) => actions.setActiveNavigationStep,
    () => {
      window.scrollTo(0, 0);
    },
  ),
  startSimulation: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const updatePayload = getPayloadForSimulationType(payload);
      const res = await injections.simulationService.startSimulation(updatePayload);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  setSimulationSellRateMappings: action((state, payload) => {
    const update = payload.length > 0 ? { [payload[0].simulationId]: payload } : {};
    state.simulationSellRateMappings = {
      ...state.simulationSellRateMappings,
      ...update,
    };
  }),
  fetchSimulationSellRateMappings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const res = await injections.simulationService.fetchSimulationSellRateMappings(payload);
      actions.setSimulationSellRateMappings(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  setSimulationSellRateZoneMappings: action((state, payload) => {
    state.simulationSellRateZoneMappings[`${payload.simulationId}_${payload.sellCarrierRateId}`] = payload.zones;
  }),
  // mappings representing those used for a simulation simulation
  fetchSimulationSellRateZoneMappings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const { sellCarrierRateId, simulationId } = payload;
      const res = await injections.simulationService.fetchSimulationSellRateZoneMappings(
        simulationId,
        sellCarrierRateId,
      );
      actions.setSimulationSellRateZoneMappings(res);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  createSimulationSellRateZoneMappings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const res = await injections.simulationService.createSimulationSellRateZoneMappings(payload);
      return res;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  // raw search of mappings,
  fetchCarrierSellRateZoneMappings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.simulationActionStart();
      const res = await injections.simulationService.searchCarrierSellRateMappings(payload);
      actions.setCarrierSellRateZoneMappings(res.mappings);
      return res.mappings;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  deleteCarrierSellRateMappings: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.simulationActionStart();
      const delRes = await injections.simulationService.deleteSellRateMappings(payload);
      const { carrierId, zoneId, deletedRecords, sellRateCarrierServiceMethodId } = delRes;
      if (deletedRecords) {
        const carrierSellRateZoneMappings = getState().carrierSellRateZoneMappings;
        actions.setCarrierSellRateZoneMappings(
          carrierSellRateZoneMappings.filter((val) => {
            // filter the deleted record from state
            // update this to handle bulk deletes etc
            return (
              carrierId !== val.carrierId ||
              zoneId !== val.zoneId ||
              sellRateCarrierServiceMethodId !== val.sellRateCarrierServiceMethodId
            );
          }),
        );
      }
      return delRes;
    } catch (error) {
      actions.simulationActionFailure({
        error,
        httpCode: parseStatusCode(error),
        message: error.message,
        asNotification: true,
      });
      throw error;
    }
  }),
  setCarrierSellRateZoneMappings: action((state, payload) => {
    state.carrierSellRateZoneMappings = payload;
  }),
  carrierSellRateZoneMappings: [],
};
